import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValueEvent, useMotionValue, useSpring, useAnimationFrame } from "framer-motion";

const ChatStack = ({ scrollYProgress, isMobile }) => {
  const mobileY = useTransform(scrollYProgress, [0, 1], ["0%", "-65%"]);
  const desktopY = useTransform(scrollYProgress, [0, 1], ["35%", "-35%"]);
  const containerY = isMobile ? mobileY : desktopY;

  const scenes = [
    { role: "Doctor", text: "Here you go! You'll receive the prescription directly on your phone via Hyperapt." },
    { role: "Receptionist", text: "I’ve shared the receipt on your phone via Hyperapt. Your medicines are being packed, so please sit back and relax." },
    { role: "Wife", text: "Medicines are coming right here? We don't need to go out and search. This is so convenient!" },
    { role: "Delivery Guy", text: "Here are your medicines sir, right on time!" },
    { role: "Husband", text: "Perfect! Got medicines in minutes, with the prescription and receipt on my phone." }
  ];

  return (
    <motion.div style={{ y: containerY }} className="w-full max-w-md flex flex-col gap-4 relative pt-12 lg:pt-0 pb-12 lg:pb-0">
      {scenes.map((scene, idx) => {
        const start = idx === 0 ? 0 : idx * 0.2;
        const end = idx === 0 ? 1 : start + 0.1;
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const opacity = useTransform(scrollYProgress, [start, end], idx === 0 ? [1, 1] : [0, 1]);
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const y = useTransform(scrollYProgress, [start, end], idx === 0 ? [0, 0] : [20, 0]);

        const isPatient = scene.role === "Wife" || scene.role === "Husband";

        return (
          <motion.div
            key={idx}
            style={{ opacity, y }}
            className={`p-4 md:p-5 w-[85%] relative shadow-[0_8px_30px_rgb(0,0,0,0.08)] backdrop-blur-xl border border-web-border/80 ${isPatient
              ? "self-end bg-white/80 rounded-3xl rounded-tr-sm"
              : "self-start bg-[#F9F8F6]/80 rounded-3xl rounded-tl-sm"
              }`}
          >
            <div className={`text-xs font-bold tracking-wide mb-1.5 ${isPatient ? "text-web-text/40" : "text-web-primary"}`}>
              {scene.role}
            </div>
            <p className="font-web-serif text-lg text-web-text leading-relaxed">
              {scene.text}
            </p>
          </motion.div>
        );
      })}
    </motion.div>
  );
};

const WebHome = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    handleResize(); // Init
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    document.title = "Hyperapt - Home";
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", "Hyperapt smart pharmacy - medicines delivered in minutes");
    else {
      const meta = document.createElement("meta");
      meta.name = "description";
      meta.content = "Hyperapt smart pharmacy - medicines delivered in minutes";
      document.head.appendChild(meta);
    }
    window.scrollTo(0, 0);
  }, []);

  const videoRef = useRef(null);

  const rawProgress = useMotionValue(0);
  const scrollYProgress = useSpring(rawProgress, { stiffness: 300, damping: 30, restDelta: 0.001 });
  const videoScale = useTransform(scrollYProgress, [0, 1], [0.85, 1.35]);

  useEffect(() => {
    // Force iOS Safari to fetch the video metadata immediately
    if (videoRef.current) {
      videoRef.current.load();
    }
  }, []);

  useEffect(() => {
    const handleWheel = (e) => {
      // Ignore horizontal scrolling
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;

      const currentProg = rawProgress.get();

      // Only hijack when we are at the very top of the page
      if (window.scrollY <= 5) {
        if (e.deltaY > 0 && currentProg >= 1) {
          // Let native scroll take over to scroll down the page
          return;
        }
        // Hijack EVERYTHING else at the top to prevent trackpad gesture lock-in
        e.preventDefault();

        if (e.deltaY > 0) {
          rawProgress.set(Math.min(1, currentProg + e.deltaY * 0.0006));
        } else if (e.deltaY < 0) {
          rawProgress.set(Math.max(0, currentProg + e.deltaY * 0.0006));
        }
      }
    };

    let lastY = 0;
    const handleTouchStart = (e) => { lastY = e.touches[0].clientY; };
    const handleTouchMove = (e) => {
      const currentProg = rawProgress.get();
      const deltaY = lastY - e.touches[0].clientY;
      lastY = e.touches[0].clientY;

      if (window.scrollY <= 5) {
        if (deltaY > 0 && currentProg >= 1) {
          return;
        }
        e.preventDefault();

        if (deltaY > 0) {
          rawProgress.set(Math.min(1, currentProg + deltaY * 0.001));
        } else if (deltaY < 0) {
          rawProgress.set(Math.max(0, currentProg + deltaY * 0.001));
        }
      }
    };

    // passive: false is required to allow e.preventDefault()
    window.addEventListener("wheel", handleWheel, { passive: false });
    window.addEventListener("touchstart", handleTouchStart, { passive: false });
    window.addEventListener("touchmove", handleTouchMove, { passive: false });

    return () => {
      window.removeEventListener("wheel", handleWheel);
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
    };
  }, [rawProgress]);

  const targetTime = useRef(0);

  // Keep track of desired time
  useMotionValueEvent(scrollYProgress, "change", (latest) => {
    if (videoRef.current && Number.isFinite(videoRef.current.duration)) {
      targetTime.current = latest * videoRef.current.duration;
    }
  });

  // Apply time in the animation loop for buttery smooth performance
  useAnimationFrame(() => {
    if (videoRef.current) {
      const diff = targetTime.current - videoRef.current.currentTime;
      // Only seek if the difference is larger than a tiny threshold to avoid decoder thrashing
      if (Math.abs(diff) > 0.03) {
        videoRef.current.currentTime = targetTime.current;
      }
    }
  });

  // Robust initialization for iOS Safari & Mobile browsers
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.load();

    const kickstartVideo = () => {
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.then(() => {
          video.pause();
        }).catch(() => {
          // Play prevented, ignore silently
        });
      }
    };

    if (video.readyState >= 3) {
      kickstartVideo();
    } else {
      video.addEventListener("canplay", kickstartVideo, { once: true });
    }
  }, []);

  const ctaRef = useRef(null);
  const { scrollYProgress: ctaProgress } = useScroll({
    target: ctaRef,
    offset: ["start 90%", "center center"]
  });
  const ctaPadding = useTransform(ctaProgress, [0, 1], ["5vw", "0vw"]);
  const ctaPaddingY = useTransform(ctaProgress, [0, 1], ["6rem", "0rem"]);
  const ctaGap = useTransform(ctaProgress, [0, 1], ["2rem", "0rem"]);
  const ctaRadius = useTransform(ctaProgress, [0, 1], ["40px", "0px"]);

  const vpRef = useRef(null);
  const { scrollYProgress: vpProgress } = useScroll({
    target: vpRef,
    offset: ["start 85%", "center center"]
  });
  const vpPaddingCard = useTransform(vpProgress, [0, 1], ["2rem", "0rem"]);
  const vpTextPadding = useTransform(vpProgress, [0, 1], ["0rem", "2rem"]);
  const vpImgRadius = useTransform(vpProgress, [0, 1], ["16px", "0px"]);

  const [activeExpectFeature, setActiveExpectFeature] = useState(0);

  const expectFeatures = [
    {
      title: "Zero Friction Orders",
      desc: "No more pharmacy hassles. Prescriptions are instantly saved and medications are delivered in minutes. Your health journey just got effortless.",
      img: "expect_zerofriction.png"
    },
    {
      title: "Smart Refill Reminders",
      desc: "Never miss a critical dose. We intelligently notify you exactly when your chronic medications are about to run out.",
      img: "expect_smartrefill.png"
    },
    {
      title: "Instant Digital Prescriptions",
      desc: "Get digital prescriptions instantly from our partner doctors to save time and never miss to bring a hardcopy for next visit.",
      img: "expect_digitalprescription.png"
    },
    {
      title: "Instant Digital Receipts",
      desc: "Receipts are instantly delivered to your WhatsApp for completely hassle-free insurance claims and record-keeping.",
      img: "expect_digitalreceipt.png"
    }
  ];

  const buttonOpacity = useTransform(scrollYProgress, [0.95, 1], [0, 1]);
  const buttonPointerEvents = useTransform(scrollYProgress, [0.95, 1], ["none", "auto"]);
  const logoLeft = useTransform(scrollYProgress, [0.95, 1], ["50%", "0%"]);
  const logoX = useTransform(scrollYProgress, [0.95, 1], ["-50%", "0%"]);

  // --- Logo Rotation Logic ---
  const isLogoRotating = useTransform(scrollYProgress, [0.99, 1], [0, 1]);
  const smoothedRotationSpeed = useSpring(isLogoRotating, {
    stiffness: 40,
    damping: 15
  });

  const currentLogoRotation = useMotionValue(0);
  useAnimationFrame((time, delta) => {
    const speed = smoothedRotationSpeed.get() * 0.05; // 0.05 deg/ms
    currentLogoRotation.set(currentLogoRotation.get() + speed * delta);
  });

  return (
    <div className="min-h-screen bg-web-bg text-web-text font-web-sans flex flex-col selection:bg-web-primary selection:text-white">

      {/* Header - Sleek, centered initially, then spaced */}
      <header className="fixed top-0 w-full z-50 bg-white/90 backdrop-blur-md transition-all duration-300 h-20 flex items-center">
        <div className="max-w-7xl mx-auto px-4 w-full h-full flex items-center">
          <div className="relative w-full flex items-center justify-end h-full">

            <motion.div
              style={{ left: logoLeft, x: logoX }}
              className="absolute flex items-center h-full"
            >
              <Link to="/home" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
                <motion.img 
                  style={{ rotate: currentLogoRotation }}
                  src="/logo.png" 
                  alt="hyperapt" 
                  className="h-8 w-auto origin-center" 
                />
                <span className="font-web-serif font-semibold text-[36px] tracking-normal text-web-text">
                  hyperapt
                </span>
              </Link>
            </motion.div>

            <motion.div
              style={{ opacity: buttonOpacity, pointerEvents: buttonPointerEvents }}
              className="flex items-center gap-2 md:gap-4"
            >
              <Link to="/partner" className="px-4 md:px-5 py-2 text-sm font-medium text-web-text hover:text-web-primary transition-colors hidden sm:block">
                For Doctors
              </Link>
              <Link to="/trackorder" className="px-4 md:px-5 py-2 text-sm font-medium text-white bg-web-primary rounded-full hover:bg-web-primary/90 transition-colors shadow-sm shadow-web-primary/20">
                Track Order
              </Link>
            </motion.div>

          </div>
        </div>
      </header>

      <main className="flex-1 w-full relative pt-20">

        {/* Cinematic Scroll-Driven Hero (Hijacked) */}
        <section className="relative w-full bg-[#FEFEFE] border-b border-web-border overflow-hidden">
          <div className="max-w-7xl mx-auto w-full flex flex-col lg:flex-row items-center justify-between p-4 py-4 gap-4 lg:gap-8">
            {/* Video Container */}
            <div className="w-full lg:w-1/2 flex items-center justify-center relative z-10 pointer-events-none">
              <motion.div style={{ scale: videoScale }} className="w-full aspect-square max-w-[500px] flex items-center justify-center origin-center">
                <video
                  ref={videoRef}
                  src="/website_assets/heroweb.mp4"
                  className="w-full h-full object-contain mix-blend-multiply"
                  muted
                  playsInline
                  preload="auto"
                  disablePictureInPicture
                  disableRemotePlayback
                />
              </motion.div>
            </div>

            {/* Text Overlay Container */}
            <div className="w-full lg:w-1/2 relative flex items-start lg:items-center justify-center z-20 h-[280px] lg:h-auto overflow-hidden lg:overflow-visible [mask-image:linear-gradient(to_bottom,transparent,black_15%,black_85%,transparent)] [-webkit-mask-image:linear-gradient(to_bottom,transparent,black_15%,black_85%,transparent)] lg:[mask-image:none] lg:[-webkit-mask-image:none]">
              <ChatStack scrollYProgress={scrollYProgress} isMobile={isMobile} />
            </div>
          </div>
        </section>

        {/* Section 1: Value Propositions */}
        <section ref={vpRef} className="py-24 lg:py-32 px-4 max-w-7xl mx-auto bg-web-bg relative z-30">
          <div className="text-center mb-16 lg:mb-24">
            <h2 className="font-web-serif text-4xl lg:text-5xl font-bold mb-6 text-web-text">Hyperapt, Loved by All</h2>
            <p className="text-xl text-web-secondary-dark max-w-2xl mx-auto font-light">Experience the future of consultation & pharmacy with uncompromised quality and unprecedented speed.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { title: "Trusted by Doctors", desc: "No compromise on medicines. 100% genuine and authentic.", img: "/website_assets/trusted.png" },
              { title: "Medicines in Minutes", desc: "Get prescription medicines quickly and hassle-free right after your consultation.", img: "/website_assets/medicine.png" },
              { title: "Personalised Medicines", desc: "Clear printed dosage instructions on each medicine.", img: "/website_assets/dosagemedicine.png" },
              { title: "Discounted Prices", desc: "Enjoy exclusive savings on every prescribed medicine.", img: "/website_assets/discount.png" }
            ].map((prop, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                style={{
                  paddingTop: vpPaddingCard,
                  paddingLeft: vpPaddingCard,
                  paddingRight: vpPaddingCard,
                  paddingBottom: "2rem"
                }}
                className="bg-white rounded-3xl shadow-sm border border-web-border hover:shadow-xl transition-shadow duration-300 group overflow-hidden flex flex-col"
              >
                <motion.div
                  style={{ borderRadius: vpImgRadius }}
                  className="aspect-square w-full bg-web-bg mb-8 overflow-hidden relative"
                >
                  <img src={prop.img} alt={prop.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </motion.div>

                <motion.div style={{ paddingLeft: vpTextPadding, paddingRight: vpTextPadding }}>
                  <h3 className="font-web-serif text-2xl font-bold mb-3">{prop.title}</h3>
                  <p className="text-web-text/80 leading-relaxed font-light">{prop.desc}</p>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Section 2: Testimonials Marquee */}
        <section className="py-20 bg-[#f4f2ec] overflow-hidden border-y border-web-border relative z-30">
          <div className="max-w-7xl mx-auto px-4 mb-12 text-center">
            <h2 className="font-web-serif text-3xl font-bold">What Our Patients Say</h2>
          </div>

          {(() => {
            const patientReviews1 = [
              `"In-Minutes delivery is magic! Clinic se bahar nikla aur dawai ghar pe thi." - Rahul S.`,
              `"The printed instructions are a lifesaver. Ekdum clear and helpful!" - Priya M.`,
              `"Ajun paryant itki fast service kadhi baghitli nahi. Khupach chan!" - Sandeep K.`,
              `"माझी रोजची औषधं आता वेळेवर मिळतात. मला बाहेर जायची अजिबात गरज पडत नाही. खूपच छान सोय!" - Sunita J.`,
              `"અરે ભાઈ, એકદમ જોરદાર સર્વિસ! દવાઓ લેવા હવે ક્યાંય લાઈનમાં ઉભા રહેવાની જરૂર નથી." - Hitesh P.`,
              `"Honestly, this is the best pharmacy experience I've ever had. Getting everything sorted right inside the clinic is so convenient." - Rohan Mehta`,
              `"Khupach fast delivery! Clinic chya baher yeiparyant medicines tayar hotya. Ekdum bhari service." - Sameer P.`,
              `"Romba fast service! No need to wait at the medical shop anymore. Fantastic experience." - Meena T.`,
              `"दवाइयां लेवण कठे ई जावण री जरूरत कोनी पड़ी। एकदम बढ़िया काम करियो सा!" - Suresh R.`,
              `"The printed dosage times on the packets are a game changer! No more guessing when to take what." - Kavita S.`,
              `"Sotti bolchi, darun byapar! Doctor dekhiye berote na berotei osudh hate peye gelam." - Souvik D.`,
              `"ಕ್ಲಿನಿಕ್ ಇಂದ ಹೊರಬರುವ ಮುನ್ನವೇ ಮೆಡಿಸಿನ್ಸ್ ಸಿಕ್ತು. ತುಂಬಾನೇ ಫಾಸ್ಟ್ ಡೆಲಿವರಿ!" - Ramesh V.`,
              `"I was amazed by how quickly they processed everything. Highly recommend their service to anyone who values their time." - Karan V.`,
            ];

            const patientReviews2 = [
              `"Premium experience. The automated digital receipts save my family so much time." - Anjali D.`,
              `"Sakkath fast delivery. Clinic inda horage barodu late, medicines already manege bandittu!" - Pooja N.`,
              `"Super smooth process from start to finish. I love how I don't have to carry paper prescriptions around anymore." - Sneha Patil`,
              `"अशी फास्ट सर्व्हिस मी पहिल्यांदाच पाहिली. क्लिनिकमधून बाहेर पडायच्या आत औषधं मिळाली!" - Santosh K.`,
              `"Aushadhanchya packets var sagli mahiti chaan dileli aste. Aajoba aaji na pan samjayla soppa zala." - Rohan M.`,
              `"Ekdum jordar service! Parchi lakhai ane dawai sidhi hath ma. Bahu saras!" - Neha Patel`,
              `"डॉक्टरांनी औषध लिहून दिल्या दिल्या काही मिनिटांतच मला औषधं मिळाली. खरोखरच अफाट सर्व्हिस!" - Vikas D.`,
              `"Top-notch service! The medicines arrived perfectly packed, and the whole experience was completely frictionless." - Aditya C.`,
              `"மருந்தகத்தில் வரிசையில் நிற்க வேண்டிய அவசியமே இல்லை. மிக விரைவான சேவை!" - Karthik R.`,
              `"Mala aushadhansathi medical cha chakkar maraychi garaj nahi. Gharchya ghari sagla milta." - Pallavi K.`,
              `"এত তাড়াতাড়ি ওষুধ পাব ভাবতেই পারিনি। একদম অসাধারণ সার্ভিস!" - Ananya B.`,
              `"क्लीनिक से बाहर निकलते ही दवाइयां मिल गईं! सर्विस बहुत ही कमाल की है।" - Amit S.`,
              `"It's so relieving to not have to hunt for medicines after a long doctor's visit. They really understand patient convenience." - Divya M.`,
            ];

            return (
              <div className="flex flex-col gap-10">
                <div className="relative flex overflow-x-hidden group w-full">
                  <div className="animate-marquee-infinite-fast whitespace-nowrap flex items-center w-max">
                    <div className="flex items-center gap-8 px-4">
                      {patientReviews1.map((review, i) => (
                        <React.Fragment key={i}>
                          <span className="text-xl md:text-2xl font-web-serif italic text-web-text/80 px-8">
                            {review}
                          </span>
                          <span className="text-web-primary">✦</span>
                        </React.Fragment>
                      ))}
                    </div>
                    <div className="flex items-center gap-8 px-4">
                      {patientReviews1.map((review, i) => (
                        <React.Fragment key={`copy2-${i}`}>
                          <span className="text-xl md:text-2xl font-web-serif italic text-web-text/80 px-8">
                            {review}
                          </span>
                          <span className="text-web-primary">✦</span>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="relative flex overflow-x-hidden group w-full">
                  <div className="animate-marquee-infinite whitespace-nowrap flex items-center w-max">
                    <div className="flex items-center gap-8 px-4">
                      {patientReviews2.map((review, i) => (
                        <React.Fragment key={i}>
                          <span className="text-xl md:text-2xl font-web-serif italic text-web-text/80 px-8">
                            {review}
                          </span>
                          <span className="text-web-primary">✦</span>
                        </React.Fragment>
                      ))}
                    </div>
                    <div className="flex items-center gap-8 px-4">
                      {patientReviews2.map((review, i) => (
                        <React.Fragment key={`copy2-${i}`}>
                          <span className="text-xl md:text-2xl font-web-serif italic text-web-text/80 px-8">
                            {review}
                          </span>
                          <span className="text-web-primary">✦</span>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </section>

        {/* Section: Get More Than Expected */}
        <section className="py-24 lg:py-32 bg-white relative z-30 border-t border-web-border overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 flex flex-col lg:flex-row gap-16 lg:gap-24 items-center">

            {/* Left Side: Accordion/List */}
            <div className="w-full lg:w-1/2 flex flex-col">
              <h2 className="font-web-serif text-4xl lg:text-5xl font-bold mb-12 text-web-text tracking-tight">
                Get more than <br className="hidden lg:block" /> expected
              </h2>

              <div className="flex flex-col gap-2">
                {expectFeatures.map((feature, idx) => {
                  const isActive = activeExpectFeature === idx;
                  return (
                    <div
                      key={idx}
                      onClick={() => setActiveExpectFeature(idx)}
                      className={`cursor-pointer transition-all duration-300 p-6 rounded-2xl border ${isActive
                        ? 'bg-[#EFEBE1]/60 border-web-secondary/30 shadow-sm'
                        : 'bg-transparent border-transparent hover:bg-gray-50'
                        }`}
                    >
                      <h3 className={`text-xl font-medium transition-colors ${isActive ? 'text-web-text' : 'text-web-text/60'}`}>
                        {feature.title}
                      </h3>
                      <div className={`overflow-hidden transition-all duration-500 ease-in-out ${isActive ? 'max-h-40 opacity-100 mt-3' : 'max-h-0 opacity-0 mt-0'}`}>
                        <p className="text-web-text/70 font-light leading-relaxed">
                          {feature.desc}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right Side: Image Display */}
            <div className="w-full lg:w-1/2 flex justify-center lg:justify-end">
              <div className="relative w-full max-w-md aspect-square rounded-[40px] overflow-hidden shadow-2xl border border-web-border/50 bg-[#F9F8F6] flex items-center justify-center group">
                {expectFeatures.map((feature, idx) => (
                  <img
                    key={idx}
                    src={`/website_assets/${feature.img}`}
                    alt={feature.title}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${activeExpectFeature === idx ? 'opacity-100 z-10' : 'opacity-0 z-0'
                      }`}
                    onError={(e) => e.target.style.display = 'none'}
                  />
                ))}
                {/* Fallback Text for Missing Images */}
                <span className="text-web-text/30 font-web-serif italic text-sm absolute z-0 text-center px-8">
                  (Image needed: {expectFeatures[activeExpectFeature].img})
                </span>
              </div>
            </div>

          </div>
        </section>

        {/* Section 3: CTA Track & Reorder */}
        <section ref={ctaRef} className="w-full bg-web-bg relative z-30 overflow-hidden">
          <motion.div
            style={{
              paddingLeft: ctaPadding,
              paddingRight: ctaPadding,
              paddingTop: ctaPaddingY,
              paddingBottom: ctaPaddingY,
              gap: ctaGap
            }}
            className="flex flex-col md:flex-row w-full mx-auto h-auto md:h-[400px] lg:h-[500px]"
          >
            {/* Card 1 */}
            <motion.div
              style={{ borderRadius: ctaRadius }}
              className="w-full md:w-1/2 h-full bg-white border border-web-border p-10 lg:p-14 shadow-lg relative overflow-hidden group flex flex-col justify-center"
            >
              <div className="absolute top-0 right-0 w-64 h-64 bg-web-secondary/20 rounded-full blur-3xl -mr-20 -mt-20 transition-transform group-hover:scale-110"></div>
              <h2 className="font-web-serif text-3xl lg:text-4xl font-bold mb-4 relative z-10">Track Your Order</h2>
              <p className="text-lg text-web-text/70 mb-8 relative z-10 max-w-sm">Keep an eye on your medicines as they make their way to you.</p>
              <Link to="/trackorder" className="inline-flex items-center gap-2 bg-web-text text-white px-8 py-4 rounded-full font-medium hover:bg-black transition-colors relative z-10 self-start">
                Track Now
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg>
              </Link>
            </motion.div>

            {/* Card 2 */}
            <motion.div
              style={{ borderRadius: ctaRadius }}
              className="w-full md:w-1/2 h-full bg-web-primary text-white p-10 lg:p-14 shadow-lg relative overflow-hidden group flex flex-col justify-center"
            >
              <div className="absolute top-0 right-0 w-64 h-64 bg-black/10 rounded-full blur-3xl -mr-20 -mt-20 transition-transform group-hover:scale-110"></div>
              <h2 className="font-web-serif text-3xl lg:text-4xl font-bold mb-4 relative z-10 text-white">Re-order in Seconds</h2>
              <p className="text-lg text-white/80 mb-8 relative z-10 max-w-sm">Finished your medicines? Ordering again is just a tap away.</p>
              <Link to="tel:9004581717" className="inline-flex items-center gap-2 bg-white text-web-primary px-8 py-4 rounded-full font-medium hover:bg-web-bg transition-colors relative z-10 shadow-xl self-start">
                Call Care Center
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
              </Link>
            </motion.div>
          </motion.div>
        </section>

        {/* Section 4: FAQ (Accordion) */}
        <section className="py-24 bg-white border-t border-web-border relative z-30">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="font-web-serif text-4xl font-bold text-center mb-16">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {[
                { q: "How fast will I get my medicines?", a: "Clinic deliveries are completed within a few minutes directly inside the clinic, Home deliveries depends on the distance of given address and Collect orders can be picked up from the pick-up point at any time right after placing the order." },
                { q: "Are all medicines authentic?", a: "Yes, we partner only with certified pharmacies and guarantee 100% authenticity on all prescriptions." },
                { q: "How do I use the personalized dosage instructions?", a: "Each medicine packet comes with clear, printed instructions indicating exactly when and how to take it." },
                { q: "What if I need help with my order?", a: "You can reach out to our Pharma Assistants anytime via our Care Center." }
              ].map((faq, i) => (
                <details key={i} className="group border border-web-border rounded-2xl bg-web-bg overflow-hidden [&_summary::-webkit-details-marker]:hidden">
                  <summary className="flex cursor-pointer items-center justify-between p-6 font-web-serif text-xl font-medium text-web-text">
                    {faq.q}
                    <span className="transition duration-300 group-open:-rotate-180">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                      </svg>
                    </span>
                  </summary>
                  <div className="px-6 pb-6 text-web-text/70 font-light leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Footer - Sleek & Minimal */}
        <footer className="bg-web-bg text-web-text py-8 border-t border-web-border relative z-30">
          <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-6">
            <Link to="/home" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <img src="/logo.png" alt="hyperapt" className="h-8 w-auto opacity-90" />
              <span className="font-web-serif font-semibold text-[32px] tracking-normal text-web-text">
                hyperapt
              </span>
            </Link>

            <nav className="flex flex-wrap justify-center gap-6 text-sm text-web-text/70 font-light">
              <Link to="/trackorder" className="hover:text-web-primary transition-colors">Track Order</Link>
              <Link to="/partner" className="hover:text-web-primary transition-colors">For Doctors</Link>
              <Link to="/terms" className="hover:text-web-primary transition-colors">Terms of Service</Link>
              <Link to="/privacypolicy" className="hover:text-web-primary transition-colors">Privacy Policy</Link>
              <Link to="/returnpolicy" className="hover:text-web-primary transition-colors">Return Policy</Link>
            </nav>

            <div className="text-sm text-web-text/50 font-light">
              © {new Date().getFullYear()} Hyperapt. All rights reserved.
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default WebHome;